import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { UserPlus, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so we only fetch /api/admin/counsellors once per page load.
let _cachedCounsellors = null;
let _cachedFetch = null;

async function fetchCounsellors(force = false) {
  if (!force && _cachedCounsellors) return _cachedCounsellors;
  if (!force && _cachedFetch) return _cachedFetch;
  _cachedFetch = (async () => {
    try {
      const res = await fetch(`${API}/api/admin/counsellors`, { credentials: 'include' });
      if (!res.ok) return [];
      const d = await res.json();
      _cachedCounsellors = d.counsellors || [];
      return _cachedCounsellors;
    } finally {
      _cachedFetch = null;
    }
  })();
  return _cachedFetch;
}

/**
 * Compact assignee dropdown for any resource (lead/recruiter/student).
 *
 * Props:
 *  - resourceType: 'lead' | 'recruiter' | 'student'
 *  - resourceId:   string id used in the API path
 *  - currentAssigneeId: string|null
 *  - onAssigned:   optional callback({assigned_to})
 *  - compact:      bool - if true, render small chip
 */
export default function AssignSelect({ resourceType, resourceId, currentAssigneeId, onAssigned, compact = false }) {
  const [counsellors, setCounsellors] = useState(_cachedCounsellors || []);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (counsellors.length === 0) {
      fetchCounsellors().then(setCounsellors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every time the dropdown is opened, refresh the counsellor list so a
  // staff member that was deleted (or just added) doesn't linger in stale UI.
  useEffect(() => {
    if (open) {
      fetchCounsellors(true).then(setCounsellors);
    }
  }, [open]);

  const current = counsellors.find((c) => c.id === currentAssigneeId);
  const triggerRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Re-compute popover position whenever it opens or the page scrolls/resizes
  // so we can render it OUTSIDE any overflow-hidden ancestor. Also caps its
  // max height and flips it above the trigger when there isn't enough space
  // below — otherwise rows near the page bottom render an unreachable dropdown.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const popoverWidth = 224; // matches w-56
      const left = Math.max(8, Math.min(r.right - popoverWidth, window.innerWidth - popoverWidth - 8));
      const margin = 12;
      const spaceBelow = window.innerHeight - r.bottom - margin;
      const spaceAbove = r.top - margin;
      const flip = spaceBelow < 220 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(160, Math.min(400, flip ? spaceAbove : spaceBelow));
      const top = flip ? Math.max(margin, r.top - 6 - maxHeight) : r.bottom + 6;
      setPos({ top, left, maxHeight });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);
  const pathMap = {
    lead: `/api/admin/leads/${resourceId}/assign`,
    recruiter: `/api/admin/recruiters/${resourceId}/assign`,
    student: `/api/admin/students/${resourceId}/assign`,
  };

  async function assignTo(userId) {
    setSaving(true);
    try {
      const res = await fetch(`${API}${pathMap[resourceType]}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: userId }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Assign failed');
      setOpen(false);
      onAssigned && onAssigned({ assigned_to: userId });
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="inline-block" data-testid={`assign-select-${resourceType}-${resourceId}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={saving}
        className={`inline-flex items-center gap-1.5 transition rounded-full border ${
          current
            ? 'bg-violet-50 border-violet-200 text-violet-800 hover:border-violet-400'
            : 'bg-tge-cream-soft border-tge-cream text-tge-muted hover:border-tge-gold/50 hover:text-tge-navy'
        } ${compact ? 'text-[10px] px-2 py-0.5' : 'text-[11px] px-2.5 py-1'}`}
        data-testid={`assign-trigger-${resourceId}`}
      >
        <UserPlus size={compact ? 10 : 12} />
        {current ? current.name || current.email : 'Unassigned'}
      </button>
      {open && pos && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[70] w-56 bg-white border border-tge-cream rounded-xl shadow-xl overflow-hidden flex flex-col"
            style={{ top: pos.top, left: pos.left, maxHeight: pos.maxHeight }}
            data-testid={`assign-dropdown-${resourceId}`}
          >
            <div className="px-3 py-2 border-b border-tge-cream text-[10px] uppercase tracking-wider text-tge-muted font-semibold shrink-0">
              Assign to
            </div>
            <div className="flex-1 overflow-y-auto">
              <button
                type="button"
                onClick={() => assignTo(null)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-tge-cream-soft flex items-center justify-between text-tge-muted"
              >
                <span>Unassigned</span>
                {!currentAssigneeId && <Check size={13} className="text-tge-gold" />}
              </button>
              {counsellors.length === 0 && (
                <div className="px-3 py-3 text-[11px] text-tge-muted italic">No counsellors found. Add staff first.</div>
              )}
              {counsellors.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => assignTo(c.id)}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-tge-cream-soft flex items-center justify-between"
                >
                  <span>
                    <span className="block text-tge-navy font-medium">{c.name}</span>
                    <span className="block text-[10px] text-tge-muted">{c.email} · {c.role}</span>
                  </span>
                  {currentAssigneeId === c.id && <Check size={13} className="text-tge-gold" />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
